import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

// Initialize Stripe - Vite requires VITE_ prefix for client-side env vars
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const CheckoutForm = ({ plan, billingCycle, user, paymentMethod }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    upiId: ''
  });
  const [errors, setErrors] = useState({});
  const [cardErrors, setCardErrors] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  });

  const API_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:4000';

  const finalPrice = billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
  const totalAmount = billingCycle === 'yearly' ? finalPrice * 12 : finalPrice;
  const gst = Math.round(totalAmount * 0.18);
  const grandTotal = totalAmount + gst;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCardChange = (field) => (event) => {
    if (event.error) {
      setCardErrors(prev => ({ ...prev, [field]: event.error.message }));
    } else {
      setCardErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (paymentMethod === 'upi') {
      if (!formData.upiId || !formData.upiId.includes('@')) {
        newErrors.upiId = 'Please enter a valid UPI ID';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== PAYMENT SUBMISSION STARTED ===');
    console.log('Payment Method:', paymentMethod);
    console.log('Stripe loaded:', !!stripe);
    console.log('Elements loaded:', !!elements);
    console.log('Plan ID:', plan?.id);
    console.log('Grand Total:', grandTotal);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);
    console.log('Loading set to true');

    try {
      if (paymentMethod === 'card') {
        if (!stripe || !elements) {
          alert('Stripe is not loaded yet. Please wait and try again.');
          setLoading(false);
          return;
        }

        console.log('Creating payment intent...');

        // Create payment intent on your backend
        const response = await fetch(`${API_URL}/api/payment/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            planId: plan.id,
            billingCycle,
            amount: grandTotal
          })
        });

        const data = await response.json();
        console.log('Payment Intent Response:', data);

        if (!data.success) {
          throw new Error(data.message || 'Failed to create payment intent');
        }

        const { clientSecret } = data;

        console.log('Confirming card payment...');

        // Confirm card payment
        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              name: user?.name || 'Customer',
              email: user?.email || ''
            }
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        console.log('Payment Intent Status:', paymentIntent.status);

        if (paymentIntent.status === 'succeeded') {
          console.log('Confirming subscription...');

          // Update user subscription on backend
          const confirmResponse = await fetch(`${API_URL}/api/payment/confirm-subscription`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              paymentIntentId: paymentIntent.id,
              planId: plan.id,
              billingCycle
            })
          });

          const confirmData = await confirmResponse.json();
          console.log('Confirmation Response:', confirmData);

          if (confirmData.success) {
            alert('Payment successful! Welcome to Pro.');
            navigate('/user-profile');
          } else {
            throw new Error(confirmData.message || 'Failed to confirm subscription');
          }
        }
      } else if (paymentMethod === 'upi') {
        const response = await fetch(`${API_URL}/api/payment/create-upi-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            planId: plan.id,
            billingCycle,
            amount: grandTotal,
            upiId: formData.upiId
          })
        });

        const data = await response.json();
        
        if (data.success) {
          alert('Payment successful! Welcome to Pro.');
          navigate('/user-profile');
        } else {
          throw new Error(data.message || 'Payment failed');
        }
      } else if (paymentMethod === 'netbanking') {
        const response = await fetch(`${API_URL}/api/payment/create-netbanking-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            planId: plan.id,
            billingCycle,
            amount: grandTotal
          })
        });

        const data = await response.json();
        
        if (data.success && data.redirectUrl) {
          window.location.href = data.redirectUrl;
        } else {
          throw new Error(data.message || 'Payment failed');
        }
      }
    } catch (error) {
      console.error('Payment failed:', error);
      alert(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1a1a1a',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {paymentMethod === 'card' && (
        <div className="space-y-4">
          {/* Card Number */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Card Number
            </label>
            <div className="p-4 border-2 border-border rounded-lg bg-card">
              <CardNumberElement 
                options={cardElementOptions}
                onChange={handleCardChange('cardNumber')}
              />
            </div>
            {cardErrors.cardNumber && (
              <p className="text-sm text-error mt-2">{cardErrors.cardNumber}</p>
            )}
          </div>

          {/* Expiry and CVC */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Expiry Date
              </label>
              <div className="p-4 border-2 border-border rounded-lg bg-card">
                <CardExpiryElement 
                  options={cardElementOptions}
                  onChange={handleCardChange('cardExpiry')}
                />
              </div>
              {cardErrors.cardExpiry && (
                <p className="text-sm text-error mt-2">{cardErrors.cardExpiry}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CVC
              </label>
              <div className="p-4 border-2 border-border rounded-lg bg-card">
                <CardCvcElement 
                  options={cardElementOptions}
                  onChange={handleCardChange('cardCvc')}
                />
              </div>
              {cardErrors.cardCvc && (
                <p className="text-sm text-error mt-2">{cardErrors.cardCvc}</p>
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Test card: 4242 4242 4242 4242 | Expiry: 12/34 | CVV: 123
          </p>
        </div>
      )}

      {paymentMethod === 'upi' && (
        <Input
          label="UPI ID"
          name="upiId"
          placeholder="yourname@upi"
          value={formData.upiId}
          onChange={handleInputChange}
          error={errors.upiId}
          description="Enter your UPI ID (e.g., 9876543210@paytm)"
          required
        />
      )}

      {paymentMethod === 'netbanking' && (
        <div className="p-8 bg-muted/30 rounded-lg text-center">
          <Icon name="Building" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground">
            You will be redirected to your bank's website to complete the payment
          </p>
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        className="w-full bg-gradient-primary hover:opacity-90"
        loading={loading}
        disabled={loading || (paymentMethod === 'card' && !stripe)}
        iconName="Lock"
        iconPosition="left"
      >
        {loading ? 'Processing...' : `Pay ₹${grandTotal}`}
      </Button>

      {/* Security Notice */}
      <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
        <Icon name="Shield" size={16} />
        <span>Secure payment powered by Stripe</span>
      </div>

      {/* Debug Info */}
      {!stripe && paymentMethod === 'card' && (
        <div className="text-xs text-warning text-center">
          ⚠️ Loading Stripe... Please wait.
        </div>
      )}
    </form>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plan, billingCycle } = location.state || {};

  const [user, setUser] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const API_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:4000';

  useEffect(() => {
    const loadUser = async () => {
      // Load user data from localStorage
      const stored = localStorage.getItem('user');
      if (stored) {
        try {
          setUser(JSON.parse(stored));
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }

      // Also try to fetch from server
      try {
        const res = await fetch(`${API_URL}/api/user/data`, { 
          credentials: 'include' 
        });
        const data = await res.json();
        if (data.success) {
          const userData = {
            name: data.userData?.name,
            email: data.userData?.email,
            isVerified: data.userData?.isAccountVerified
          };
          setUser(userData);
        }
      } catch (e) {
        console.error('Error loading user:', e);
      }
    };

    loadUser();

    // Redirect if no plan selected
    if (!plan) {
      console.warn('No plan selected, redirecting to plans page');
      navigate('/plans');
    }
  }, [plan, navigate, API_URL]);

  if (!plan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const finalPrice = billingCycle === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
  const totalAmount = billingCycle === 'yearly' ? finalPrice * 12 : finalPrice;
  const gst = Math.round(totalAmount * 0.18);
  const grandTotal = totalAmount + gst;

  console.log('Stripe Promise:', stripePromise);
  console.log('Stripe Public Key:', import.meta.env.VITE_STRIPE_PUBLIC_KEY);

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to pricing</span>
          </button>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Complete Your Purchase
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Payment Form */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                Payment Details
              </h2>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-3">
                  Select Payment Method
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'card'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-border/80'
                    }`}
                  >
                    <Icon name="CreditCard" size={24} color={paymentMethod === 'card' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} className="mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Card</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('upi')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'upi'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-border/80'
                    }`}
                  >
                    <Icon name="Smartphone" size={24} color={paymentMethod === 'upi' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} className="mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">UPI</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('netbanking')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'netbanking'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-border/80'
                    }`}
                  >
                    <Icon name="Building" size={24} color={paymentMethod === 'netbanking' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} className="mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Net Banking</p>
                  </button>
                </div>
              </div>

              {/* Payment Form with Stripe Elements */}
              <Elements stripe={stripePromise}>
                <CheckoutForm 
                  plan={plan} 
                  billingCycle={billingCycle} 
                  user={user}
                  paymentMethod={paymentMethod}
                />
              </Elements>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-xl border border-border p-6 sticky top-4">
              <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                Order Summary
              </h2>

              {/* Plan Details */}
              <div className="mb-6 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-foreground">{plan.name} Plan</h3>
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-medium">
                    {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>

              {/* Pricing Breakdown */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} subscription
                  </span>
                  <span className="text-foreground font-medium">₹{totalAmount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">GST (18%)</span>
                  <span className="text-foreground font-medium">₹{gst}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="flex justify-between text-sm text-success">
                    <span>Yearly discount (20%)</span>
                    <span className="font-medium">-₹{Math.round(plan.price * 12 * 0.2)}</span>
                  </div>
                )}
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="text-foreground font-semibold">Total</span>
                  <span className="text-foreground font-bold text-xl">₹{grandTotal}</span>
                </div>
              </div>

              {/* Features Included */}
              <div className="border-t border-border pt-4">
                <h4 className="text-sm font-medium text-foreground mb-3">Included features:</h4>
                <div className="space-y-2">
                  {plan.features.filter(f => f.included).slice(0, 4).map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Money-back Guarantee */}
              <div className="mt-6 p-4 bg-success/5 border border-success/20 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Shield" size={20} color="var(--color-success)" className="flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">
                      7-Day Money-Back Guarantee
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Not satisfied? Get a full refund within 7 days, no questions asked.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;